"use client";

import { useEffect, useMemo, useState } from "react";
import { Avatar } from "@/components/ui/Avatar";
import { useTranslations } from "@/contexts/I18nContext";
import type { MemberProfile } from "@/lib/models";

interface MentionPickerProps {
  members: MemberProfile[];
  query: string;
  onSelect: (member: MemberProfile) => void;
  onClose: () => void;
}

/**
 * Inline mention autocomplete picker — mirrors iOS `MentionPicker`
 * (`Views/Controls/MentionPicker.swift`) and Android `MentionSuggestionList`
 * (`ui/components/MentionPicker.kt`). Filters household members by nickname /
 * name (case-insensitive prefix), and exposes keyboard navigation:
 *
 * - ArrowUp / ArrowDown — move highlight
 * - Enter — select highlighted member
 * - Escape — close the picker
 *
 * When there are no matches the component renders nothing — no "no matches"
 * placeholder, matching the native clients.
 */
export function MentionPicker({
  members,
  query,
  onSelect,
  onClose,
}: MentionPickerProps) {
  const t = useTranslations();
  const [rawHighlightIndex, setHighlightIndex] = useState(0);

  // Pets are excluded from mention candidates to mirror the native clients
  // (Android filters `memberType != "pet"`). Filtering uses case-insensitive
  // prefix matching against nickname first, name second.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => {
      if (m.memberType === "pet") return false;
      const nick = m.nickname?.toLowerCase() ?? "";
      const name = m.name?.toLowerCase() ?? "";
      if (!q) return Boolean(nick || name);
      return nick.startsWith(q) || name.startsWith(q);
    });
  }, [members, query]);

  // Clamp the highlight index inline rather than via a sync-state effect so we
  // never render past the end of `filtered` when the list shrinks. (Avoids
  // `react-hooks/set-state-in-effect`.)
  const highlightIndex =
    filtered.length === 0
      ? 0
      : Math.max(0, Math.min(rawHighlightIndex, filtered.length - 1));

  // Global key listener — the picker is rendered as a sibling of the
  // textarea, so we listen at the window level rather than on the picker
  // itself (the textarea keeps focus, not the popover).
  useEffect(() => {
    if (filtered.length === 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((idx) =>
          filtered.length === 0 ? 0 : (idx + 1) % filtered.length,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((idx) =>
          filtered.length === 0
            ? 0
            : (idx - 1 + filtered.length) % filtered.length,
        );
      } else if (e.key === "Enter") {
        const member = filtered[highlightIndex];
        if (member) {
          e.preventDefault();
          onSelect(member);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [filtered, highlightIndex, onSelect, onClose]);

  if (filtered.length === 0) return null;

  return (
    <div
      role="listbox"
      aria-label={t("posts.compose.mention.aria")}
      className="absolute left-0 right-0 z-10 mt-1 max-h-48 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg"
    >
      {filtered.map((member, idx) => {
        const display =
          member.nickname || member.name || t("posts.compose.mention.fallback");
        const isHighlighted = idx === highlightIndex;
        return (
          <button
            key={member.uid ?? `${idx}-${display}`}
            type="button"
            role="option"
            aria-selected={isHighlighted}
            onMouseEnter={() => setHighlightIndex(idx)}
            // Use `onMouseDown` instead of `onClick` so the textarea doesn't
            // lose focus before we apply the mention. Without this, the
            // textarea blur fires first and the cursor position is lost.
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(member);
            }}
            className={`flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors ${
              isHighlighted ? "bg-gray-100" : "bg-white"
            } hover:bg-gray-100`}
          >
            <Avatar src={member.profile_url} alt={display} size={28} />
            <span className="text-gray-900">
              {display.charAt(0).toUpperCase() + display.slice(1)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
